/**
 * The deck toolbar (slides, posters, web pages — DeliverableEditor): the tools to make objects
 * always, and then only what fits what is selected, like Google Slides — text (font, size, B I U,
 * colour, alignment, lists, spacing) for text and shapes with text, fill and outline for shapes,
 * colour / weight / dash / arrow tips for lines, crop / remove background / replace for pictures,
 * arrangement, opacity and the rest for anything selected; with nothing selected, the slide's
 * background and the canvas around the pages. Rendered by the toolbars' own component (Toolbar.tsx),
 * so it looks like the LyX rows in the Classic interface and like Google's in the Modern one.
 */
import { useEffect, useState } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import { mathPreview, type ToolButton, type Palette } from '../app/Toolbar';
import { ColorGrid } from '../app/ColorGrid';
import type { SelItem, Tool, Paint, ArrangeHow, DeliverableKind, NodeCmd, GroupLayout } from './protocol';
import { DEFAULT_GROUP_LAYOUT } from './grouplayout';
import { ASPECTS } from './runtime/crop';
import { fmtPt, ptToPx, pxToPt, sizePt, stepPt } from './units';
import { drawingToolbar } from './drawingToolbar';
import { type DeckInk, type DeckInkPatch, LASSO_KINDS } from './deckink';
import { lassoGroups } from './lassobar';
import { PH_ROLES, PH_LABELS, type PhRole } from './decklayouts';
import { bundledFont, firstFontFamily } from './deckfonts';
import './deckicons';

export interface DeckTextActions {
  family(css: string | null, google?: string): void;
  /** points (1 pt = 4/3 CSS px) */
  size(pt: number): void;
  bold(): void; italic(): void; underline(): void; strike(): void;
  color(c: string | null): void;
  highlight(c: string | null): void;
  align(a: 'left' | 'center' | 'right' | 'justify'): void;
  valign(v: 'flex-start' | 'center' | 'flex-end'): void;
  list(kind: 'ul' | 'ol'): void;
  lineHeight(v: string): void;
  box?(anchor: 'fixed' | 'left' | 'center' | 'right'): void;
  step(n: number): void;
  clear(): void;
}

export interface DeckCtx {
  kind: DeliverableKind;
  fontWarning?(css: string): string | null;
  /** The deck's text font, for the warning when no object is selected. */
  defaultFont?: string | null;
  pageLabel?: string;
  tool: Tool;
  setTool(t: Tool): void;
  readOnly: boolean;
  sel: SelItem[];
  editingText: boolean;
  undo(): void; redo(): void;
  insertImage(): void;
  text: DeckTextActions;
  paint(p: Paint): void;
  arrange(how: ArrangeHow): void;
  groupLayout(layout: GroupLayout): void;
  image: {
    /** crop mode is on (the picture's frame, Done / Cancel) */
    cropping: boolean;
    crop(): void; resetCrop(): void; removeBackground(): void; replace(): void;
    /** the picture is an SVG drawing in the project: open it in the drawing editor (the documents' pop-up), else null */
    editSvg: (() => void) | null;
    /** cropped at once to an aspect / a round frame (crop mode: the frame changed) */
    shape(aspect: number | null, shape?: 'rect' | 'circle'): void;
    done(commit: boolean): void;
  };
  remove(): void;
  /** the slide's background colour — or (`every`) every slide's */
  slideBackground(c: string | null, every?: boolean): void;
  /** the guides (guides.ts): shown or not, and what can be done with them (the slide's right-click menu's Guides) */
  guides: { shown: boolean; items(): { label: string; action: () => void; active?: boolean }[] };
  newSlide(): void;
  /** the layouts a new slide can take (LayoutPicker.tsx: the deck's own, those made of its design, like a slide of it, blank) — or (`apply`) the slide shown can be put in */
  layoutPicker(close: () => void, apply?: boolean): ComponentChildren;
  /**
   * The deck's master (deckmaster.ts): the object selected put on every slide, or — one that is —
   * unlinked from its copies, taken off the other slides, deleted from all; the texts like the one
   * selected on the other slides (`similar` of them) made to look like it
   */
  master?: { on(): void; unlink(): void; removeOthers(): void; deleteAll(): void; similar: number; lookAlike(): void };
  /** the theme view (Edit theme: the objects on every slide, the deck's colours, fonts and layouts — DeliverableEditor), on or not; in it Background is every slide's */
  theme?: { on: boolean; toggle(): void; disabled?: boolean };
  /** placeholders (decklayouts.ts): one of a role put on the slide; a picture put into the empty picture placeholder selected */
  placeholders?: { insert(role: PhRole): void; picture(): void };
  /** LyX's formula editor (MathOverlay): open on a formula of the text, a new formula at the caret, its commands, the math panels; a displayed one numbered and labelled */
  math: { editing: boolean; insert(display: boolean): void; exec(cmd: string, ...args: unknown[]): void; panels: { id: string; title: string; palette: Palette }[]; display: boolean; number(): void; label(): void };
  /** LaTeX's citations, references and overlays on slides (core html/refs.ts) */
  latex: {
    /** a link on the selected words (or the address put in) */
    link(): void;
    /** the citation dialog (absent where the project's bibliography cannot be searched) */
    cite?(): void;
    /** a reference to a labelled equation (\eqref) */
    reference(): void;
  };
  /** the Animation pane (what appears and disappears on each click when presenting): open, toggled */
  animate: { open: boolean; toggle(): void };
  /** the node editor (a path's points): on, its selected nodes, its commands */
  nodes: { editing: boolean; selected: number; total: number; cmd(c: NodeCmd): void };
  /** comments and suggestions (ReviewPane): a new comment, the selected object's suggestion to accept or reject */
  review?: { comment(): void; suggestion: { by: string; summary: string; accept(): void; reject(): void } | null };
  /**
   * Draw: the pen's and the highlighter's presets (deckink.ts); `draw(true)` takes the pen in hand and
   * opens Draw's row (`open`), `draw(false)` puts it away; the colours used on the slide (the lasso's
   * filter offers them); the selected strokes thinner or thicker, converted (the lasso's Convert)
   */
  ink: {
    presets: DeckInk; set(p: DeckInkPatch): void; draw(on: boolean): void; open?: boolean;
    /** the Draw row shown again or folded away, what is in hand kept (drawingToolbar `picked`); `held`: folded so */
    fold?(folded: boolean): void; held?: boolean;
    lassoColors?(): string[]; thicken?(k: number): void; convert?(as: 'shapes' | 'notes' | 'formula'): void;
  };
}

/** the node editor's row: Inkscape's node toolbar */
function nodeGroups(c: DeckCtx): ToolButton[][] {
  const n = c.nodes, none = n.selected === 0, x = n.cmd;
  return [
    [
      { id: 'dk-node-add', title: 'Add a node between the selected ones (or double-click the outline)', icon: 'dk-node-add', disabled: none, action: () => x('insert') },
      { id: 'dk-node-del', title: 'Delete the selected nodes (Delete)', icon: 'dk-node-del', disabled: none, action: () => x('delete') },
    ],
    [
      { id: 'dk-node-corner', title: 'Corner: the handles move on their own (double-click a node to switch)', icon: 'dk-node-corner', disabled: none, action: () => x('corner') },
      { id: 'dk-node-smooth', title: 'Smooth: the handles stay on one line', icon: 'dk-node-smooth', disabled: none, action: () => x('smooth') },
      { id: 'dk-seg-line', title: 'Make the segments between the selected nodes straight', icon: 'dk-seg-line', disabled: n.selected < 2, action: () => x('straight') },
      { id: 'dk-seg-curve', title: 'Make the segments between the selected nodes curves', icon: 'dk-seg-curve', disabled: n.selected < 2, action: () => x('curve') },
    ],
    [
      { id: 'dk-node-break', title: 'Break the path at the selected nodes', icon: 'dk-node-break', disabled: none, action: () => x('break') },
      { id: 'dk-node-join', title: 'Join two selected end nodes', icon: 'dk-node-join', disabled: n.selected !== 2, action: () => x('join') },
      { id: 'dk-path-close', title: 'Close or open the path', icon: 'dk-path-close', action: () => x('close') },
    ],
    [
      { id: 'dk-node-all', title: 'Select all nodes (Ctrl+A)', icon: `${n.selected}/${n.total}`, action: () => x('all') },
      { id: 'dk-node-done', title: 'Done (Enter or Esc, or click beside the path)', icon: 'Done', active: true, action: () => x('done') },
    ],
  ];
}

/** Inkscape's Path menu for the selection */
function pathItems(c: DeckCtx, sel: SelItem[]) {
  const many = sel.length >= 2;
  const items = [
    ...(many ? [
      { label: 'Union', html: ICON('dk-union'), title: 'One shape of all of them', action: () => c.arrange('union') },
      { label: 'Difference', html: ICON('dk-difference'), title: 'The bottom shape minus the ones above it', action: () => c.arrange('difference') },
      { label: 'Intersection', html: ICON('dk-intersection'), title: 'Only where they all overlap', action: () => c.arrange('intersection') },
      { label: 'Exclusion', html: ICON('dk-exclusion'), title: 'Where an odd number of them overlap', action: () => c.arrange('exclusion') },
      { label: 'Combine (Ctrl+K)', html: ICON('dk-combine'), title: 'One path made of their outlines', action: () => c.arrange('combine') },
    ] : []),
    { label: 'Break apart (Ctrl+Shift+K)', html: ICON('dk-breakapart'), title: 'A path of several pieces: one object per piece', action: () => c.arrange('break-apart') },
    { label: 'Object to path', html: ICON('dk-topath'), title: 'A rectangle, an ellipse, a polygon… as a path whose points can be edited', action: () => c.arrange('to-path') },
    { label: 'Edit points (N)', html: ICON('dk-nodes'), title: 'Drag the nodes and handles of the path (or double-click it)', action: () => c.arrange('edit-nodes') },
    { label: 'Simplify (Ctrl+L)', html: ICON('dk-simplify'), title: 'Fewer nodes, smoother curves', action: () => c.arrange('simplify') },
    { label: 'Reverse direction', html: ICON('dk-reverse'), title: 'The path runs the other way (arrowheads swap ends)', action: () => c.arrange('reverse') },
  ];
  return items;
}

const MATH_PANEL_FACE: Record<string, string> = {
  latex_greek: '\\alpha', latex_brel: '\\leq', latex_bop: '\\otimes', latex_arrow: '\\rightarrow', latex_misc: '\\infty', latex_varsz: '\\sum',
  latex_dots: '\\cdots', latex_deco: '\\hat{a}', functions: '\\sin', font: '\\mathbb{R}', latex_delim: '\\lfloor\\rfloor', latex_ams_rel: '\\leqslant',
  latex_ams_nrel: '\\nleq', latex_ams_ops: '\\boxtimes', latex_ams_arrows: '\\rightrightarrows', latex_ams_misc: '\\square', 'frac-square': '\\frac{a}{b}', 'sqrt-square': '\\sqrt{x}', space: '\\square\\,\\square', style: '\\displaystyle',
};

/** the math row while a formula is open: LyX's math toolbar, the commonest first, then its panels */
function mathGroups(c: DeckCtx): ToolButton[][] {
  const x = c.math.exec;
  const face = (tex: string) => mathPreview(tex) ?? undefined;
  return [
    [
      { id: 'm-frac', title: 'Fraction (Alt+M F)', icon: 'mfrac', action: () => x('insert', '\\frac{#0}{}') },
      { id: 'm-sup', title: 'Superscript (^)', icon: 'sup', action: () => x('moveToSuperscript') },
      { id: 'm-sub', title: 'Subscript (_)', icon: 'sub', action: () => x('moveToSubscript') },
      { id: 'm-sqrt', title: 'Square root (Alt+M S)', icon: 'msqrt', action: () => x('insert', '\\sqrt{#0}') },
      { id: 'm-sum', title: 'Sum (Alt+M U)', icon: 'msum', action: () => x('insert', '\\sum') },
      { id: 'm-int', title: 'Integral (Alt+M I)', icon: 'mint', action: () => x('insert', '\\int') },
    ],
    [
      { id: 'm-paren', title: '( ) that grow with their content (Alt+M ()', icon: '( )', html: face('\\left(\\square\\right)'), action: () => x('delim', '(', ')') },
      { id: 'm-bracket', title: '[ ] (Alt+M [)', icon: '[ ]', html: face('\\left[\\square\\right]'), action: () => x('delim', '[', ']') },
      { id: 'm-brace', title: '{ } (Alt+M {)', icon: '{ }', html: face('\\left\\{\\square\\right\\}'), action: () => x('delim', '\\{', '\\}') },
      { id: 'm-abs', title: '| | (Alt+M |)', icon: '| |', html: face('\\left|\\square\\right|'), action: () => x('delim', '|', '|') },
      { id: 'm-matrix', title: 'Matrix (2 × 2)', icon: 'matrix', html: face('\\begin{pmatrix}a&b\\\\c&d\\end{pmatrix}'), action: () => x('insert', '\\begin{pmatrix}#0 & \\\\ & \\end{pmatrix}') },
      { id: 'm-cases', title: 'Cases (Alt+M C)', icon: 'cases', html: face('\\begin{cases}a & \\\\ b & \\end{cases}'), action: () => x('insert', '\\begin{cases}#0 & \\\\ & \\end{cases}') },
      { id: 'm-text', title: 'Text in the formula (Ctrl+M)', icon: 'Tx', action: () => x('text') },
    ],
    ...(c.math.display ? [[
      { id: 'm-number', title: 'Number the equation, or not (Alt+M N) — LaTeX\'s equation environment', icon: '(1)', action: c.math.number },
      { id: 'm-label', title: 'Label… — the name \\eqref{…} refers to it by (numbers it)', icon: 'Label', action: c.math.label },
    ] as ToolButton[]] : []),
    c.math.panels.filter(p => MATH_PANEL_FACE[p.id]).map(p => ({ id: 'mp-' + p.id, title: p.title, icon: p.title, html: face(MATH_PANEL_FACE[p.id]), palette: p.palette })),
  ];
}

/** Bundled choices are embedded; other web fonts load from Google Fonts when first used. */
export const DECK_FONTS: { label: string; css: string | null; google?: string }[] = [
  { label: 'Default', css: null },
  { label: 'Fira Sans (bundled)', css: '"Fira Sans", Helvetica, Arial, sans-serif' },
  { label: 'Carlito (bundled)', css: 'Carlito, Helvetica, Arial, sans-serif' },
  { label: 'CMU Sans (bundled)', css: '"CMU Sans", Helvetica, Arial, sans-serif' },
  { label: 'Inter', css: 'Inter, Helvetica, Arial, sans-serif', google: 'Inter' },
  { label: 'Roboto', css: 'Roboto, Helvetica, Arial, sans-serif', google: 'Roboto' },
  { label: 'Open Sans', css: '"Open Sans", Helvetica, Arial, sans-serif', google: 'Open Sans' },
  { label: 'Lato', css: 'Lato, Helvetica, Arial, sans-serif', google: 'Lato' },
  { label: 'Montserrat', css: 'Montserrat, Helvetica, Arial, sans-serif', google: 'Montserrat' },
  { label: 'Poppins', css: 'Poppins, Helvetica, Arial, sans-serif', google: 'Poppins' },
  { label: 'Source Sans 3', css: '"Source Sans 3", Helvetica, Arial, sans-serif', google: 'Source Sans 3' },
  { label: 'Merriweather', css: 'Merriweather, Georgia, serif', google: 'Merriweather' },
  { label: 'Lora', css: 'Lora, Georgia, serif', google: 'Lora' },
  { label: 'Playfair Display', css: '"Playfair Display", Georgia, serif', google: 'Playfair Display' },
  { label: 'EB Garamond', css: '"EB Garamond", Georgia, serif', google: 'EB Garamond' },
  { label: 'Source Code Pro', css: '"Source Code Pro", Menlo, Consolas, monospace', google: 'Source Code Pro' },
  { label: 'Helvetica / Arial', css: 'Helvetica, Arial, sans-serif' },
  { label: 'Georgia', css: 'Georgia, "Times New Roman", serif' },
  { label: 'Courier', css: '"Courier New", Courier, monospace' },
];

export const SHAPES: { tool: Tool; label: string }[] = [
  { tool: 'rect', label: 'Rectangle' }, { tool: 'roundrect', label: 'Rounded rectangle' }, { tool: 'ellipse', label: 'Ellipse' },
  { tool: 'triangle', label: 'Triangle' }, { tool: 'diamond', label: 'Diamond' }, { tool: 'star', label: 'Star' }, { tool: 'hexagon', label: 'Hexagon' },
];
// (freehand strokes: Draw, a tool of its own)
export const LINES: { tool: Tool; label: string }[] = [{ tool: 'line', label: 'Line' }, { tool: 'arrow', label: 'Arrow' }, { tool: 'bezier', label: 'Path: click for corners, drag for curves (B)' }];

const firstFamily = firstFontFamily;

/** − [size] + : Google's font size box, in points */
function SizeBox({ pt, onSet }: { pt: number; onSet: (pt: number) => void }) {
  const [text, setText] = useState(pt ? String(pt) : '');
  useEffect(() => { setText(pt ? String(pt) : ''); }, [pt]);
  const commit = () => { const v = Math.round(Number(text.replace(',', '.').replace(/\s*pt$/i, '')) * 2) / 2; if (v >= 3 && v <= 600 && v !== pt) onSet(v); else setText(pt ? String(pt) : ''); };
  const step = (d: 1 | -1) => onSet(stepPt(pt || 12, d));
  return (
    <span class="dk-size" data-dk-size>
      <button type="button" class="tb-btn" title="Smaller (Ctrl+Shift+,)" onMouseDown={e => e.preventDefault()} onClick={() => step(-1)}>−</button>
      <input value={text} inputMode="decimal" aria-label="Font size (pt)" onInput={e => setText((e.target as HTMLInputElement).value)}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commit(); (e.target as HTMLInputElement).blur(); } if (e.key === 'Escape') { setText(pt ? String(pt) : ''); (e.target as HTMLInputElement).blur(); } }}
        onBlur={commit} />
      <button type="button" class="tb-btn" title="Larger (Ctrl+Shift+.)" onMouseDown={e => e.preventDefault()} onClick={() => step(1)}>+</button>
    </span>
  );
}

function Slider({ value, onSet, label }: { value: number; onSet: (v: number) => void; label: string }) {
  const [v, setV] = useState(value);
  return (
    <div class="dk-slider">
      <input type="range" min={0} max={100} value={v} aria-label={label} onInput={e => setV(Number((e.target as HTMLInputElement).value))} onChange={e => onSet(Number((e.target as HTMLInputElement).value))} />
      <span>{v}%</span>
    </div>
  );
}

function InlineStepPicker({ onSet, close }: { onSet: (step: number) => void; close: () => void }) {
  const [value, setValue] = useState('2');
  const step = Number(value);
  return <form class="dk-inline-step" onSubmit={e => { e.preventDefault(); if (Number.isSafeInteger(step) && step >= 1) { onSet(step); close(); } }}>
    <label>Appear on step <input type="number" min="1" step="1" value={value} aria-label="Appear on step" onInput={e => setValue((e.target as HTMLInputElement).value)} /></label>
    <button type="submit" class="small-btn" disabled={!Number.isSafeInteger(step) || step < 1} onMouseDown={e => e.preventDefault()}>Apply to selected words</button>
    <span>Select words in one paragraph first. Step 1 shows them from the start; step 2 is the first click.</span>
  </form>;
}

function GroupLayoutPicker({ layout, onSet, close }: { layout: GroupLayout; onSet: (layout: GroupLayout) => void; close: () => void }) {
  const [value, setValue] = useState(layout);
  const free = value.direction === 'free';
  const valid = Number.isFinite(value.gap) && value.gap >= 0 && value.gap <= 2000;
  return <form class="dk-group-layout" onSubmit={e => { e.preventDefault(); if (valid) { onSet(value); close(); } }}>
    <label>Layout <select aria-label="Group layout" value={value.direction} onChange={e => setValue({ ...value, direction: e.currentTarget.value as GroupLayout['direction'] })}>
      <option value="free">Free</option><option value="row">Row</option><option value="column">Column</option>
    </select></label>
    <label>Gap (px) <input type="number" min="0" max="2000" step="1" aria-label="Group gap (px)" disabled={free} value={value.gap} onInput={e => setValue({ ...value, gap: e.currentTarget.valueAsNumber })} /></label>
    <label>Align items <select aria-label="Group item alignment" disabled={free} value={value.align} onChange={e => setValue({ ...value, align: e.currentTarget.value as GroupLayout['align'] })}>
      <option value="flex-start">Start</option><option value="center">Centre</option><option value="flex-end">End</option><option value="stretch">Stretch</option>
    </select></label>
    <label><input type="checkbox" disabled={free} checked={value.equal} onChange={e => setValue({ ...value, equal: e.currentTarget.checked })} />{value.direction === 'column' ? 'Equal heights' : 'Equal widths'}</label>
    <button type="submit" class="small-btn" disabled={!valid}>Apply</button>
    <span>Ctrl+click to edit an item. Free layout lets you position items independently.</span>
  </form>;
}

const swatchIcon = (glyph: string, color: string | null) => `<svg viewBox="0 0 16 16">${glyph}<rect x="2" y="12.6" width="12" height="2.6" rx="0.5" fill="${color ?? 'none'}" stroke="${color ? 'none' : 'currentColor'}" stroke-width="0.7"/></svg>`;

/** a named run of the contextual tools: what they act on, named as the selection's label on the canvas (runtime edit.ts selectionLabel) */
export interface ToolSection { label: string; groups: ToolButton[][] }

/** what the fill / border tools act on, in a word */
function paintLabel(items: SelItem[]): string {
  const ks = new Set(items.map(s => s.kind));
  if (ks.size === 1) return ks.has('svg') ? 'SVG' : ks.has('svgpart') ? 'SVG part' : 'Shape';
  return 'Fill & border';
}

/**
 * The quick menu beside the selection (QuickBar.tsx): the operations used most, taken from the
 * toolbar's own sections (so they act and look the same) — text: bold, italic, colour, size,
 * alignment; shapes: fill and border; lines: colour, weight, arrowheads; pictures: crop and background;
 * then group / ungroup and arrange, `extra` (duplicate, comment) and delete. Empty while points, a formula or a crop are edited.
 * Over `words` selected in a text being retyped: what is done to words — font, size, bold … highlight, a link —, then `extra`.
 */
const QUICK: string[][] = [['dk-ph-addpicture'], ['dk-b', 'dk-i', 'dk-color', 'dk-size'], ['dk-align'], ['dk-inkcolor', 'dk-ink-thinner', 'dk-ink-thicker', 'dk-ink-convert', 'dk-fill', 'dk-stroke'], ['dk-linecolor', 'dk-lineweight', 'dk-tips'], ['dk-crop', 'dk-nobg'], ['dk-group', 'dk-ungroup', 'dk-arrange']];
const QUICK_WORDS: string[][] = [['dk-font', 'dk-size'], ['dk-b', 'dk-i', 'dk-u', 'dk-color', 'dk-hl'], ['dk-href', 'dk-inline-step']];
export function quickGroups(sections: ToolSection[], extra: ToolButton[], words = false): ToolButton[][] {
  const all = new Map<string, ToolButton>();
  for (const sec of sections) for (const g of sec.groups) for (const b of g) all.set(b.id, b);
  if (all.has('dk-node-done') || all.has('dk-cropdone') || all.has('m-frac')) return [];
  const groups = (words ? QUICK_WORDS : QUICK).map(ids => ids.map(id => all.get(id)).filter((b): b is ToolButton => !!b)).filter(g => g.length);
  if (!groups.length) return [];
  // (over words, Delete would take the whole box)
  const del = words ? undefined : all.get('dk-delete');
  // its own ids (q-…): the toolbar's buttons stay one of a kind on the page
  const own = (x: ToolButton): ToolButton => ({ ...x, id: 'q-' + x.id.replace(/^dk-/, '') });
  return [...groups, [...extra, ...(del ? [del] : [])]].map(g => g.map(own));
}

export function deckToolbar(c: DeckCtx): { tools: ToolButton[][]; context: ToolButton[][]; sections: ToolSection[]; draw?: boolean } {
  const ro = c.readOnly;
  const sel = c.sel;
  const one = sel.length === 1 ? sel[0] : null;
  const shapeTool = SHAPES.find(s => s.tool === c.tool);
  const lineTool = LINES.find(s => s.tool === c.tool);
  // Draw's row: while Draw is open (its button, P, E, S) — not when the Pencil just drew by itself, nor once
  // something was picked in it (folded away, the pen still in hand: `held`)
  const drawTool = c.tool === 'pen' || c.tool === 'eraser' || c.tool === 'lasso';
  const drawing = drawTool && c.ink.open !== false;
  const held = drawTool && !drawing && !!c.ink.held && !!c.ink.fold;

  const tools: ToolButton[][] = [
    [
      { id: 'dk-undo', title: 'Undo (Ctrl+Z)', icon: 'undo', action: c.undo, disabled: ro },
      { id: 'dk-redo', title: 'Redo (Ctrl+Y)', icon: 'redo', action: c.redo, disabled: ro },
    ],
    [
      { id: 'dk-select', title: 'Select and move (V)', icon: 'dk-select', action: () => c.setTool('select'), active: c.tool === 'select' },
      { id: 'dk-text', title: 'Text box (T) — click for auto width, drag for wrapping text', icon: 'dk-text', action: () => c.setTool('text'), active: c.tool === 'text', disabled: ro },
      { id: 'dk-shapes', title: 'Shapes (R rectangle, O ellipse)', icon: shapeTool ? `dk-${shapeTool.tool}` : 'dk-shapes', active: !!shapeTool, disabled: ro,
        palette: { title: 'Shapes', cols: 4, items: SHAPES.map(s => ({ label: s.label, html: ICON(`dk-${s.tool}`), active: c.tool === s.tool, action: () => c.setTool(s.tool) })) } },
      { id: 'dk-lines', title: 'Lines (L line, A arrow, B Bézier path)', icon: lineTool ? `dk-${lineTool.tool}` : 'dk-line', active: !!lineTool, disabled: ro,
        palette: { title: 'Lines', cols: 4, items: LINES.map(s => ({ label: s.label, html: ICON(`dk-${s.tool}`), active: c.tool === s.tool, action: () => c.setTool(s.tool) })) } },
      { id: 'dk-draw', title: drawing ? 'Done drawing (Esc)' : held ? 'Drawing — click for the pens, colours and widths again (Esc or V puts the pen away)' : 'Draw (P) — pen, highlighter, eraser and lasso, stroke after stroke until Esc (a Pencil draws without it)',
        icon: 'dk-draw', active: drawing || held, disabled: ro, action: () => { if (held) c.ink.fold!(false); else c.ink.draw(!drawing); } },
      { id: 'dk-image', title: 'Picture… (or drop a picture or an SVG on the slide)', icon: 'dk-image', action: c.insertImage, disabled: ro },
      { id: 'dk-formula', title: c.editingText ? 'Formula here, in the text (Ctrl+M or $; Ctrl+Shift+M displayed)' : 'Formula — click or drag on the slide; while typing a text, Ctrl+M or $ puts one in it', icon: 'dk-formula',
        action: () => { if (c.editingText) c.math.insert(false); else c.setTool('formula'); }, active: c.tool === 'formula' || c.math.editing, disabled: ro },
      { id: 'dk-references', title: 'References — the works cited (\\cite) listed, as LaTeX\'s bibliography: click or drag on the slide', icon: 'dk-references', action: () => c.setTool('references'), active: c.tool === 'references', disabled: ro },
    ],
    ...(c.review ? [[
      { id: 'dk-comment', title: 'Comment (Ctrl+Alt+M) — on what is selected, on the words selected while typing, or on the slide', icon: 'dk-comment', action: c.review.comment, disabled: ro },
    ]] : []),
  ];

  if (ro) return { tools, context: [], sections: [] };
  if (c.math.editing) { const g = mathGroups(c); return { tools, context: g, sections: [{ label: 'Formula', groups: g }] }; }
  if (c.nodes.editing) { const g = nodeGroups(c); return { tools, context: g, sections: [{ label: 'Points', groups: g }] }; }
  const drawRow = drawing ? drawingToolbar({ ...c, lasso: { kinds: [...LASSO_KINDS], colors: () => c.ink.lassoColors?.() ?? [] }, picked: c.ink.fold && (() => c.ink.fold!(true)) }) : null;
  // (the lasso's catch: its own tools after Draw's)
  // (`draw`: the sections are the Draw row — on a touch screen a palette of its own, DeliverableEditor drawPalette)
  if (drawRow && !(c.tool === 'lasso' && sel.length)) return { tools, context: drawRow, sections: [{ label: 'Draw', groups: drawRow }], draw: true };
  // (folded away while drawing: nothing over the canvas — not the slide's tools in its place)
  if (held && !(c.tool === 'lasso' && sel.length)) return { tools, context: [], sections: [] };
  const sections: ToolSection[] = [];
  let context: ToolButton[][] = [];
  // the selected object's suggestion: accepted or rejected here, before its own tools
  const sug = c.review?.suggestion;
  if (sug) sections.push({ label: `Suggestion · ${sug.by}`, groups: [[
    { id: 'dk-accept', title: `Accept: ${sug.summary}`, icon: 'dk-accept', action: sug.accept },
    { id: 'dk-reject', title: `Reject: back to how it was (${sug.summary})`, icon: 'dk-reject', action: sug.reject },
  ]] });
  /** the groups pushed from here on belong to a section named `label` */
  const section = (label: string) => { context = []; sections.push({ label, groups: context }); };
  const textish = c.editingText || sel.some(s => s.canText || s.text);
  const shapes = sel.filter(s => s.kind === 'shape' || s.kind === 'svg' || s.kind === 'svgpart' || (s.kind === 'text' && s.style.fill));
  const lines = sel.filter(s => s.kind === 'line');
  const images = sel.filter(s => s.kind === 'image');
  const inks = sel.filter(s => s.kind === 'ink');

  if (textish) {
    section(!c.editingText && one?.kind === 'formula' ? 'Formula' : 'Text');
    const st = one?.style;
    const pt = st ? sizePt(parseFloat(st.fontSize) || 0) : 0;
    const fam = st ? firstFamily(st.fontFamily) : '';
    const warning = [...new Set(sel.filter(s => s.canText || s.text).map(s => c.fontWarning?.(s.style.fontFamily)).filter(Boolean))].join('\n');
    const bold = sel.length > 0 && sel.every(s => Number(s.style.fontWeight) >= 600);
    const italic = sel.length > 0 && sel.every(s => s.style.fontStyle === 'italic');
    const under = sel.length > 0 && sel.every(s => s.style.textDecoration.includes('underline'));
    context.push([
      { id: 'dk-font', title: warning || 'Font', icon: '', html: `<span class="dk-font-face">${warning ? '⚠ ' : ''}${escHtml(fam || 'Font')}</span>`,
        palette: { title: 'Font', list: true, cols: 1, items: DECK_FONTS.map(f => {
          const warn = f.css && !bundledFont(f.css) ? c.fontWarning?.(f.css) : null;
          return { label: f.label + (warn ? ' ⚠' : ''), title: warn || f.label, html: `<span class="dk-font-sample" style="font-family:${escAttr(f.css ?? 'inherit')}">Aa</span>`, active: !!f.css && firstFamily(f.css) === fam, action: () => c.text.family(f.css, f.google) };
        }) } },
      { id: 'dk-size', title: 'Font size (pt)', icon: '', widget: () => <SizeBox pt={pt} onSet={c.text.size} /> },
    ]);
    context.push([
      { id: 'dk-b', title: 'Bold (Ctrl+B)', icon: 'dk-bold', action: c.text.bold, active: bold },
      { id: 'dk-i', title: 'Italic (Ctrl+I)', icon: 'dk-italic', action: c.text.italic, active: italic },
      { id: 'dk-u', title: 'Underline (Ctrl+U)', icon: 'dk-underline', action: c.text.underline, active: under },
      { id: 'dk-color', title: 'Text colour', icon: '', html: swatchIcon('<text x="3.6" y="10.4" font-size="10.5" font-family="Helvetica, Arial, sans-serif" fill="currentColor">A</text>', st?.color ?? null),
        palette: { title: 'Text colour', render: close => <ColorGrid current={st?.color ?? null} close={close} onPick={col => c.text.color(col)} /> } },
      ...(c.editingText ? [{ id: 'dk-hl', title: 'Highlight colour (of the selected words)', icon: '', html: swatchIcon(ICONSVG.highlight, null),
        palette: { title: 'Highlight', render: (close: () => void) => <ColorGrid current={null} close={close} none="None" onPick={col => c.text.highlight(col)} /> } } as ToolButton] : []),
    ]);
    // what goes into a text, as in documents (the same order, icons, names and keys as their row: toolbars.tsx)
    if (c.editingText) context.push(
      [{ id: 'dk-href', title: 'Link (Ctrl+K)', icon: 'href', action: c.latex.link }],
      [
        { id: 'dk-ref', title: 'Cross-reference (Ctrl+Shift+I) — \\eqref to a labelled equation', icon: 'ref', action: c.latex.reference },
        ...(c.latex.cite ? [{ id: 'dk-cite', title: 'Citation (Ctrl+Shift+C) — \\cite, from the project\'s .bib files or found online', icon: 'cite', action: c.latex.cite } as ToolButton] : []),
      ],
      [
        { id: 'dk-math', title: 'Inline formula (Ctrl+M)', icon: 'math', action: () => c.math.insert(false) },
        { id: 'dk-dmath', title: 'Display formula (Ctrl+Shift+M)', icon: 'dmath', action: () => c.math.insert(true) },
      ],
    );
    if (c.editingText && c.kind === 'deck') context.push([
      { id: 'dk-inline-step', title: 'Appear on step… (selected words)', icon: 'dk-step',
        palette: { title: 'Selected words', render: close => <InlineStepPicker onSet={c.text.step} close={close} /> } },
    ]);
    const al = st?.textAlign ?? 'left';
    context.push([
      { id: 'dk-align', title: 'Alignment', icon: al === 'center' ? 'aligncenter' : al === 'right' || al === 'end' ? 'alignright' : 'alignleft',
        palette: { title: 'Alignment', list: true, cols: 1, items: [
          { label: 'Left (Ctrl+Shift+L)', html: ICON('alignleft'), active: al === 'left' || al === 'start', action: () => c.text.align('left') },
          { label: 'Centre (Ctrl+Shift+E)', html: ICON('aligncenter'), active: al === 'center', action: () => c.text.align('center') },
          { label: 'Right (Ctrl+Shift+R)', html: ICON('alignright'), active: al === 'right' || al === 'end', action: () => c.text.align('right') },
          { label: 'Justified (Ctrl+Shift+J)', html: ICON('alignleft'), active: al === 'justify', action: () => c.text.align('justify') },
          ...(sel.some(s => s.kind === 'shape') ? [
            { label: 'Top', html: ICON('valigntop'), action: () => c.text.valign('flex-start') },
            { label: 'Middle', html: ICON('valignmiddle'), action: () => c.text.valign('center') },
            { label: 'Bottom', html: ICON('valignbottom'), action: () => c.text.valign('flex-end') },
          ] : []),
        ] } },
      { id: 'dk-ul', title: 'Bulleted list (Ctrl+Shift+8)', icon: 'dk-bullets', action: () => c.text.list('ul') },
      { id: 'dk-ol', title: 'Numbered list (Ctrl+Shift+7)', icon: 'dk-numbers', action: () => c.text.list('ol') },
      { id: 'dk-more-text', title: 'More text options', icon: 'dk-lines',
        palette: { title: 'Text', list: true, cols: 1, items: [
          { label: 'Strikethrough', html: ICON('dk-strike'), action: c.text.strike },
          ...(c.text.box && sel.some(s => s.textBox) && !c.editingText ? [
            ...(['left', 'center', 'right'] as const).map(anchor => ({
              label: `Auto width · ${anchor === 'center' ? 'centre' : anchor} anchor`,
              active: one?.textBox === anchor, action: () => c.text.box!(anchor),
            })),
            { label: 'Fixed width · wrap text', active: one?.textBox === 'fixed', action: () => c.text.box!('fixed') },
          ] : []),
          ...['1', '1.15', '1.5', '2'].map(v => ({ label: `Line spacing ${v}`, html: ICON('dk-lines'), active: lineSpacing(st) === v, action: () => c.text.lineHeight(v) })),
          { label: 'Clear formatting (Ctrl+\\)', html: ICON('dk-clear'), action: c.text.clear },
          // (Keynote's Update style: the master's text styles, kept in the slides' own markup)
          ...(c.master && c.master.similar > 0 && one && !c.editingText ? [{ label: `Make ${c.master.similar} similar text${c.master.similar === 1 ? '' : 's'} on the other slides look like this`, html: ICON('dk-master'), action: c.master.lookAlike }] : []),
        ] } },
    ]);
  }

  const paintOf = (items: SelItem[]) => items[0]?.style;
  if (shapes.length) {
    section(paintLabel(shapes));
    const st = paintOf(shapes)!;
    const boxShape = shapes.some(s => s.kind === 'shape');
    context.push([
      { id: 'dk-fill', title: 'Fill colour', icon: '', html: swatchIcon(ICONSVG.fill, st.fill),
        palette: { title: 'Fill colour', render: close => <ColorGrid current={st.fill} close={close} none="Transparent" onPick={col => c.paint({ fill: col })} /> } },
      { id: 'dk-stroke', title: 'Border colour', icon: '', html: swatchIcon(ICONSVG.stroke, st.stroke),
        palette: { title: 'Border colour', render: close => <ColorGrid current={st.stroke} close={close} none="No border" onPick={col => c.paint({ stroke: col })} /> } },
      weightBtn('dk-weight', 'Border weight', st.strokeWidth, w => c.paint(w === 0 ? { stroke: null } : { strokeWidth: w })),
      dashBtn('dk-dash', 'Border dash', st.dash, d => c.paint({ dash: d })),
      ...(boxShape ? [{ id: 'dk-radius', title: 'Corner radius', icon: 'dk-radius',
        palette: { title: 'Corners', list: true, cols: 1, items: [0, 3, 6, 12, 24, 36].map(r => ({ label: r ? `${r} pt` : 'Square', action: () => c.paint({ radius: r ? ptToPx(r) : null }) })).concat([{ label: 'Round (ellipse)', action: () => c.paint({ radius: 9999 }) }]) } } as ToolButton] : []),
    ]);
  }
  if (lines.length) {
    section(lines.length > 1 ? 'Lines' : 'Line');
    const st = paintOf(lines)!;
    context.push([
      { id: 'dk-linecolor', title: 'Line colour', icon: '', html: swatchIcon(ICONSVG.stroke, st.stroke),
        palette: { title: 'Line colour', render: close => <ColorGrid current={st.stroke} close={close} onPick={col => c.paint({ stroke: col })} /> } },
      weightBtn('dk-lineweight', 'Line weight', st.strokeWidth, w => c.paint({ strokeWidth: w || 1 })),
      dashBtn('dk-linedash', 'Line dash', st.dash, d => c.paint({ dash: d })),
      { id: 'dk-tips', title: 'Arrowheads', icon: 'dk-tips',
        palette: { title: 'Arrowheads', list: true, cols: 1, items: [
          { label: 'None', active: !st.arrowStart && !st.arrowEnd, action: () => c.paint({ arrowStart: false, arrowEnd: false }) },
          { label: 'At the end →', active: !st.arrowStart && st.arrowEnd, action: () => c.paint({ arrowStart: false, arrowEnd: true }) },
          { label: 'At the start ←', active: st.arrowStart && !st.arrowEnd, action: () => c.paint({ arrowStart: true, arrowEnd: false }) },
          { label: 'Both ↔', active: st.arrowStart && st.arrowEnd, action: () => c.paint({ arrowStart: true, arrowEnd: true }) },
        ] } },
    ]);
  }
  if (inks.length) {
    // Draw's strokes: their ink's colour (the outline is filled with it)
    section(inks.length > 1 ? 'Strokes' : 'Ink');
    const st = inks[0].style, ink = st.stroke ?? st.fill;
    // thinner, thicker, Convert (to text, a formula, clean shapes): the lasso's (lassobar.tsx)
    const more = c.ink.thicken && c.ink.convert ? lassoGroups({
      color: null, remove: c.remove, thicken: c.ink.thicken,
      straighten: () => c.ink.convert!('shapes'), toText: () => c.ink.convert!('notes'), toFormula: () => c.ink.convert!('formula'),
    }, 'dk-ink')[0] ?? [] : [];
    context.push([
      { id: 'dk-inkcolor', title: 'Ink colour', icon: '', html: swatchIcon(ICONSVG.stroke, ink),
        palette: { title: 'Ink colour', render: close => <ColorGrid current={ink} close={close} onPick={col => { if (col) c.paint({ ink: col }); }} /> } },
      ...more,
    ]);
  }
  if (images.length === 1 || c.image.cropping) {
    const im = images[0];
    const shapes = ASPECTS.map(a => ({ label: a.label, html: a.shape === 'circle' ? ICON('dk-ellipse') : ICON('dk-crop'), action: () => c.image.shape(a.ratio, a.shape ?? 'rect') }));
    section(c.image.cropping ? 'Crop' : 'Picture');
    context.push(c.image.cropping ? [
      { id: 'dk-cropdone', title: 'Keep the crop (Enter)', icon: 'Done', action: () => c.image.done(true), active: true },
      { id: 'dk-cropcancel', title: 'Leave the picture as it was (Esc)', icon: 'Cancel', action: () => c.image.done(false) },
      { id: 'dk-cropaspect', title: 'Frame shape', icon: 'dk-crop', palette: { title: 'Crop to', list: true, cols: 1, items: shapes } },
    ] : [
      { id: 'dk-crop', title: 'Crop — drag the picture inside the frame to choose what shows (double-click a picture)', icon: 'dk-crop', action: c.image.crop },
      { id: 'dk-cropshape', title: 'Crop to a shape or an aspect ratio', icon: 'dk-radius',
        palette: { title: 'Crop to', list: true, cols: 1, items: [...shapes, ...(im?.cropped ? [{ label: 'Reset crop', html: ICON('dk-crop'), action: c.image.resetCrop }] : [])] } },
      { id: 'dk-nobg', title: 'Remove background', icon: 'dk-nobg', action: c.image.removeBackground },
      { id: 'dk-replace', title: 'Replace picture…', icon: 'dk-replace', action: c.image.replace },
      ...(c.image.editSvg ? [{ id: 'dk-editsvg', title: 'Edit drawing — the SVG in the drawing editor (double-click the picture)', icon: 'dk-pen', action: c.image.editSvg }] : []),
    ]);
  }

  // an empty picture placeholder: a picture for it (PowerPoint's icon in the box)
  if (one?.ph?.role === 'picture' && one.ph.empty && c.placeholders) {
    section('Picture placeholder');
    context.push([{ id: 'dk-ph-addpicture', title: 'Add a picture — it takes the placeholder’s place and size', icon: 'dk-image', action: c.placeholders.picture }]);
  }

  if (sel.length) {
    section('Arrange');
    const st = one?.style;
    const locked = sel.every(s => s.locked);
    const isGroup = sel.some(s => s.kind === 'group' || (s.kind === 'svgpart' && s.tag === 'g'));
    context.push([
      ...(sel.length > 1 ? [{ id: 'dk-group', title: 'Group (Ctrl+G) — moved and resized as one; Ctrl+click an object in it to move it alone', icon: 'dk-group', action: () => c.arrange('group') }] : []),
      ...(isGroup ? [{ id: 'dk-ungroup', title: 'Ungroup (Ctrl+Shift+G)', icon: 'dk-ungroup', action: () => c.arrange('ungroup') }] : []),
      ...(one?.kind === 'group' && one.tag !== 'svg' ? [{ id: 'dk-group-layout', title: 'Group layout: free, row or column, gap and alignment', icon: 'dk-group',
        palette: { title: 'Group layout', render: (close: () => void) => <GroupLayoutPicker layout={one.layout ?? DEFAULT_GROUP_LAYOUT} onSet={c.groupLayout} close={close} /> } }] : []),
      { id: 'dk-arrange', title: 'Arrange: order, align, rotate, group', icon: 'dk-arrange',
        palette: { title: 'Arrange', list: true, cols: 1, items: [
          { label: 'Bring to front (Ctrl+Shift+↑)', html: ICON('dk-arrange'), action: () => c.arrange('front') },
          { label: 'Bring forward (Ctrl+↑)', html: ICON('dk-arrange'), action: () => c.arrange('forward') },
          { label: 'Send backward (Ctrl+↓)', html: ICON('dk-arrange'), action: () => c.arrange('backward') },
          { label: 'Send to back (Ctrl+Shift+↓)', html: ICON('dk-arrange'), action: () => c.arrange('back') },
          { label: sel.length > 1 ? 'Align left edges' : 'Align left on the slide', html: ICON('dk-align'), action: () => c.arrange('align-left') },
          { label: 'Align centres', html: ICON('dk-align'), action: () => c.arrange('align-center') },
          { label: 'Align right edges', html: ICON('dk-align'), action: () => c.arrange('align-right') },
          { label: 'Align tops', html: ICON('dk-align'), action: () => c.arrange('align-top') },
          { label: 'Align middles', html: ICON('dk-align'), action: () => c.arrange('align-middle') },
          { label: 'Align bottoms', html: ICON('dk-align'), action: () => c.arrange('align-bottom') },
          { label: 'Centre on the slide horizontally', html: ICON('dk-align'), action: () => c.arrange('center-h') },
          { label: 'Centre on the slide vertically', html: ICON('dk-align'), action: () => c.arrange('center-v') },
          ...(sel.length >= 3 ? [
            { label: 'Distribute horizontally', html: ICON('dk-align'), action: () => c.arrange('distribute-h') },
            { label: 'Distribute vertically', html: ICON('dk-align'), action: () => c.arrange('distribute-v') },
          ] : []),
          { label: 'Rotate 90° clockwise', html: ICON('dk-rotate'), action: () => c.arrange('rotate-cw') },
          { label: 'Rotate 90° counter-clockwise', html: ICON('dk-rotate'), action: () => c.arrange('rotate-ccw') },
          ...(sel.some(s => s.style.rotate) ? [{ label: 'Reset rotation', html: ICON('dk-rotate'), action: () => c.arrange('rotate-reset') }] : []),
          { label: 'Flip horizontally', html: ICON('dk-flip'), action: () => c.arrange('flip-h') },
          { label: 'Flip vertically', html: ICON('dk-flip'), action: () => c.arrange('flip-v') },
          ...(sel.length > 1 ? [{ label: 'Group (Ctrl+G)', html: ICON('dk-group'), action: () => c.arrange('group') }] : []),
          ...(sel.length > 1 && sel.every(s => s.kind !== 'svgpart') ? [
            { label: 'Group in a row', html: ICON('dk-group'), action: () => c.arrange('group-row') },
            { label: 'Group in a column', html: ICON('dk-group'), action: () => c.arrange('group-column') },
          ] : []),
          ...(isGroup ? [{ label: 'Ungroup (Ctrl+Shift+G)', html: ICON('dk-ungroup'), action: () => c.arrange('ungroup') }] : []),
        ] } },
      ...(sel.some(s => s.kind === 'shape' || s.kind === 'svg' || s.kind === 'svgpart' || s.kind === 'line') ? [{
        id: 'dk-path', title: 'Path: union, difference, intersection, combine, object to path, edit points…', icon: 'dk-path',
        palette: { title: 'Path', list: true, cols: 1, items: pathItems(c, sel) } } as ToolButton] : []),
      { id: 'dk-opacity', title: 'Opacity', icon: 'dk-opacity',
        palette: { title: 'Opacity', render: () => <Slider label="Opacity" value={Math.round((st ? Number(st.opacity) : 1) * 100)} onSet={v => c.paint({ opacity: v / 100 })} /> } },
      { id: 'dk-shadow', title: 'Shadow', icon: 'dk-shadow', active: !!st?.shadow, action: () => c.paint({ shadow: !st?.shadow }) },
      ...(c.kind === 'deck' ? [animateButton(c)] : []),
      ...(c.master && one && one.slide >= 0 && c.kind === 'deck' ? [masterButton(c.master, one)] : []),
      { id: 'dk-lock', title: locked ? 'Unlock (it can be moved again)' : 'Lock (selected, not moved or resized)', icon: 'dk-lock', active: locked, action: () => c.arrange(locked ? 'unlock' : 'lock') },
      { id: 'dk-delete', title: 'Delete (Del)', icon: 'dk-delete', action: c.remove, disabled: locked },
    ]);
  } else if (c.kind !== 'page') {
    const deck = c.kind === 'deck';
    const theme = deck && c.theme?.on;
    const what = theme ? 'Theme' : c.pageLabel ?? (c.kind === 'poster' ? 'Poster' : 'Slide');
    const warning = c.defaultFont ? c.fontWarning?.(c.defaultFont) : null;
    section(what);
    // (words on the buttons, as Google Slides' Background, Layout and Edit theme: what they do is not drawn easily)
    context.push([
      { id: 'dk-bg', title: theme ? 'Background colour of every slide' : deck ? 'Background colour — of this slide, or of every slide' : `${what} background colour`, icon: 'Background',
        palette: { title: theme ? 'Background of every slide' : `${what} background`, render: close => <BackgroundPalette close={close} every={deck} always={!!theme} onPick={c.slideBackground} /> } },
      ...(deck && !theme ? [{ id: 'dk-applylayout', title: 'Layout — this slide in another layout (title slide, title and body, two columns …): its words go into the new layout’s places; or this slide saved as a layout', icon: 'Layout',
        palette: { title: 'Layout of this slide', render: (close: () => void) => c.layoutPicker(close, true) } } as ToolButton] : []),
      ...(deck && c.theme ? [{ id: 'dk-theme', icon: theme ? 'Done' : 'Edit theme', active: theme, disabled: c.theme.disabled && !theme, action: c.theme.toggle,
        title: theme ? 'Done: back to the slides (Esc)' : 'Edit theme — the slide master: what is on every slide (a logo, a bar, the slide number), the deck’s colours and fonts, its layouts; what you add or change there is on every slide' } as ToolButton] : []),
      ...(warning ? [{ id: 'dk-font-warning', icon: '⚠ Font', title: warning,
        palette: { title: 'Font portability', render: () => <div class="dl-anim-hint">{warning}</div> } } as ToolButton] : []),
    ]);
    context.push([
      { id: 'dk-guides', title: 'Guides: lines objects snap to — every slide’s (the master’s) and a slide’s own; drag one to move it (Alt: a copy), off the slide to remove it (also: right-click the slide)', icon: 'dk-guides', active: c.guides.shown,
        // (what can be done, worked out when it opens: it reads the deck's guides)
        palette: { title: 'Guides', list: true, render: close => (
          <div class="tb-popup-grid">
            {c.guides.items().map((it, i) => (
              <button key={i} type="button" class={'tb-pal-item' + (it.active ? ' active' : '')} data-guides-item={it.label} onMouseDown={e => e.preventDefault()} onClick={() => { close(); it.action(); }}>
                <span class="tb-pal-label">{it.active ? '✓ ' : ''}{it.label}</span>
              </button>
            ))}
          </div>
        ) } },
      ...(deck && !theme && c.placeholders ? [{ id: 'dk-placeholder', title: 'Placeholder — a box that says what goes in it (“Click to add title”) until something does; nothing of it shows when presenting', icon: 'dk-placeholder',
        palette: { title: 'Placeholder', list: true, cols: 1, items: PH_ROLES.map(r => ({ label: PH_LABELS[r], html: ICON('dk-ph-' + r), action: () => c.placeholders!.insert(r) })) } } as ToolButton] : []),
      ...(deck && !theme ? [animateButton(c)] : []),
    ]);
    // (a poster is one page: a "slide" added to it was invisible — no rail — and a blank page of the PDF)
    if (deck && !theme) context.push([
      { id: 'dk-newslide', title: 'New slide after this one, like it (Ctrl+M)', icon: '+ Slide', action: c.newSlide },
      // (PowerPoint's New Slide ▾, Google's + ▾: the layouts a new slide can take)
      { id: 'dk-layouts', title: 'New slide in a layout — title slide, title and body, two columns … made of this deck’s design, or one of its own', icon: '', html: '<span class="dk-sr">New slide in a layout</span>',
        palette: { title: 'New slide', render: (close: () => void) => c.layoutPicker(close) } },
    ]);
  }

  let out = sections.filter(x => x.groups.length);
  // a shape or drawing selected (not being typed into): its own tools first, then those of its text — as its label says
  if (!c.editingText && shapes.length && sel.every(x => x.kind !== 'text' && x.kind !== 'formula')) {
    const own = out.find(x => x.label === paintLabel(shapes));
    if (own) out = [own, ...out.filter(x => x !== own)];
  }
  // (a suggestion on it before anything)
  const sugSec = out.find(x => x.groups[0]?.[0]?.id === 'dk-accept');
  if (sugSec && out[0] !== sugSec) out = [sugSec, ...out.filter(x => x !== sugSec)];
  if (drawRow) out = [{ label: 'Draw', groups: drawRow }, ...out];
  return { tools, context: out.flatMap(x => x.groups), sections: out };
}

/** the Animation pane: what appears and disappears on each click when presenting (beamer's overlays, AnimationPane.tsx) */
function animateButton(c: DeckCtx): ToolButton {
  return { id: 'dk-step', title: 'Animation: objects appear and disappear click by click when presenting, in the order you click them (beamer\'s \\pause, \\only<2->)', icon: 'dk-step', active: c.animate.open, action: c.animate.toggle };
}

/** On every slide (deckmaster.ts): put there — or, for one that is, what else can be done with it */
function masterButton(m: NonNullable<DeckCtx['master']>, one: SelItem): ToolButton {
  return one.master
    ? { id: 'dk-master', title: 'On every slide — a change to it here changes it on every slide that has it', icon: 'dk-master', active: true,
      palette: { title: 'On every slide', list: true, cols: 1, items: [
        { label: 'Unlink this one — change it on this slide alone', action: m.unlink },
        { label: 'Remove from the other slides', action: m.removeOthers },
        { label: 'Delete from every slide', action: m.deleteAll },
      ] } }
    : { id: 'dk-master', title: 'Put on every slide (the slide master): a logo, a bar, a footer — changed on one slide, changed on all', icon: 'dk-master', action: m.on };
}

/** the background palette: the colours, for this slide or (a deck) every slide — `always` every slide (the theme view) */
function BackgroundPalette({ close, every: offer, always, onPick }: { close: () => void; every: boolean; always?: boolean; onPick: (c: string | null, every: boolean) => void }) {
  const [every, setEvery] = useState(false);
  return (
    <div class="dk-canvas-pal">
      {offer && !always && <label class="dk-check"><input type="checkbox" checked={every} data-dk-bg-every onChange={e => setEvery((e.target as HTMLInputElement).checked)} /> Every slide</label>}
      <ColorGrid current={null} close={close} none="As the design has it" onPick={col => onPick(col, offer && (every || !!always))} />
    </div>
  );
}

/** PowerPoint's line weights (pt), and two heavier */
const WEIGHTS = [0.25, 0.5, 0.75, 1, 1.5, 2.25, 3, 4.5, 6, 9, 12];

/** a line weight list, in points (`cur` and what `set` gets: CSS px of the slide) */
function weightBtn(id: string, title: string, cur: number, set: (px: number) => void): ToolButton {
  const now = pxToPt(cur);
  return { id, title, icon: 'dk-weight', palette: { title, list: true, cols: 1, items: [0, ...WEIGHTS].map(w => ({
    label: w ? `${fmtPt(w)} pt` : 'None',
    html: w ? `<svg viewBox="0 0 40 16" width="40"><path d="M2 8h36" stroke="currentColor" stroke-width="${Math.min(ptToPx(w), 12)}"/></svg>` : undefined,
    active: w ? Math.abs(now - w) < 0.07 : cur === 0,
    action: () => set(ptToPx(w)),
  })) } };
}

function dashBtn(id: string, title: string, cur: string, set: (d: 'solid' | 'dashed' | 'dotted') => void): ToolButton {
  const row = (d: string) => `<svg viewBox="0 0 40 16" width="40"><path d="M2 8h36" stroke="currentColor" stroke-width="2.4" stroke-linecap="${d === 'dotted' ? 'round' : 'butt'}" ${d === 'dashed' ? 'stroke-dasharray="7 4"' : d === 'dotted' ? 'stroke-dasharray="0.1 5"' : ''}/></svg>`;
  return { id, title, icon: 'dk-dash', palette: { title, list: true, cols: 1, items: (['solid', 'dashed', 'dotted'] as const).map(d => ({ label: d[0].toUpperCase() + d.slice(1), html: row(d), active: cur === d, action: () => set(d) })) } };
}

const lineSpacing = (st: SelItem['style'] | undefined) => {
  if (!st) return '';
  const lh = parseFloat(st.lineHeight), fs = parseFloat(st.fontSize);
  if (!lh || !fs) return '';
  const r = Math.round(lh / fs * 100) / 100;
  return ['1', '1.15', '1.5', '2'].find(v => Math.abs(Number(v) - r) < 0.03) ?? '';
};

const escHtml = (s: string) => s.replace(/[&<>]/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[ch]!));
const escAttr = (s: string) => s.replace(/"/g, '&quot;');

import { ICONS } from '../app/Toolbar';
import { LYX_ICONS } from '../app/lyxicons';
/** an icon as markup for palette items */
function ICON(name: string): string {
  if (LYX_ICONS[name]) return `<img src="${LYX_ICONS[name]}" alt="" width="16" height="16" style="vertical-align:middle">`;
  return ICONS[name] ? `<span class="dk-ico">${ICONS[name]}</span>` : '';
}
const ICONSVG = {
  fill: '<path d="M3 7.5l4.5-4.5 4.5 4.5-4.5 4.5z" fill="currentColor" fill-opacity="0.25" stroke="currentColor" stroke-width="1.1"/><path d="M13 8.8c.6 1 1 1.6 1 2a1 1 0 0 1-2 0c0-.4.4-1 1-2z" fill="currentColor"/>',
  stroke: '<path d="M10.5 1.8l2.4 2.4-6.6 6.6-2.9.5.5-2.9z" fill="none" stroke="currentColor" stroke-width="1.1" stroke-linejoin="round"/>',
  highlight: '<path d="M4 9.5l5.5-5.5 2.3 2.3-5.5 5.5H4z" fill="none" stroke="currentColor" stroke-width="1.1" stroke-linejoin="round"/>',
};
